import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useServiceHook from "@hooks/useServiceHook";
import { Country } from "@utils/constants/localisation";
import {
  SelectedVehicleAction,
  createInitialFilterState,
} from "./selectedVehicleReducer";
import { QuoteBuilderService } from "@services/QuoteBuilder/QuoteBuilderService";
import { validateQuote } from "@utils/quoteValidation";
import { CustomerPotentialDetailAction } from "./customerPotentialDetailReducer";
import { PricingRecordAction } from "./pricingRecordReducer";
import ModalContainer from "@components/modal/Container";
import CircularSpinner from "@components/spinners/Circular/Circular";
import {
  determineQuoteFlow,
  extractCustomerDetails,
  QuoteFlowDecision,
} from "@utils/quoteLandingHelpers";

const ForegroundLoadingSpinner = () => (
  <ModalContainer stylesCfg={{ windowTransparency: true }}>
    <LoadingSpinner />
  </ModalContainer>
);

const LoadingSpinner = () => (
  <div
    data-testid="loading-spinner"
    style={{ display: "flex", justifyContent: "center", margin: "15vh" }}
  >
    {<CircularSpinner />}
  </div>
);

type Props = {
  filterDispatch: React.Dispatch<SelectedVehicleAction>;
  customerPotentialDetailDispatch: React.Dispatch<CustomerPotentialDetailAction>;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  navigateNewQuoteTarget: string;
  navigateStatusPageTarget: string;
  navigateErrorPageTarget: string;
};

export default function QuoteLandingView({
  filterDispatch,
  customerPotentialDetailDispatch,
  pricingRecordDispatch,
  navigateNewQuoteTarget,
  navigateStatusPageTarget,
  navigateErrorPageTarget
}: Props) {
  const navigate = useNavigate();
  const { potentialId, country } = useParams() as {
    potentialId: string;
    country: Country;
  };
  const { state } = useLocation();
  const forceNewQuote = state?.forceNewQuote;

  // Clear any existing pricing record data when component mounts to prevent data leakage
  useEffect(() => {
    pricingRecordDispatch({
      type: "SET_PRICING_RECORD",
      payload: null,
    });
  }, [pricingRecordDispatch]);

  // Fetch pricing record using potential ID
  const [loading, error, errMessage, pricingRecord] = useServiceHook(
    QuoteBuilderService.getPricingRecordUsingPotential,
    [{ "force-new-quote": forceNewQuote }, country, potentialId],
    true,
    []
  );

  // Update available options when API response is received
  useEffect(() => {
    if (pricingRecord && !loading && !error) {
      // Extract and update customer details
      const customerDetails = extractCustomerDetails(
        pricingRecord.pricing_record
      );
      customerPotentialDetailDispatch({
        type: "UPDATE_MULTIPLE_FIELDS",
        payload: customerDetails,
      });

      // Determine what to do based on validation and status
      const validationError = validateQuote(pricingRecord.pricing_record);
      const decision = determineQuoteFlow(
        pricingRecord.pricing_record,
        validationError
      );

      if (decision === QuoteFlowDecision.SHOW_ERROR) {
        // Handle error case when there is no existing pricing record
        customerPotentialDetailDispatch({
          type: "UPDATE_FIELD",
          payload: {
            field: "error",
            value: validationError,
          },
        });
        navigate(navigateErrorPageTarget);
        return;
      } else if (
        decision === QuoteFlowDecision.CREATE_NEW_QUOTE ||
        forceNewQuote
      ) {
        // Handle new quote creation flow
        const filter = createInitialFilterState(pricingRecord.pricing_record);
        filterDispatch({
          type: "SET_FILTER",
          payload: filter,
        });
        navigate(navigateNewQuoteTarget);
        return;
      } else if (
        decision === QuoteFlowDecision.SHOW_EXISTING_QUOTE &&
        !forceNewQuote
      ) {
        // Handle existing quote status flow
        pricingRecordDispatch({
          type: "SET_PRICING_RECORD",
          payload: pricingRecord.pricing_record,
        });
        navigate(navigateStatusPageTarget);
      }
    }
  }, [
    pricingRecord,
    loading,
    error,
    filterDispatch,
    customerPotentialDetailDispatch,
    navigateNewQuoteTarget,
    navigate,
    pricingRecordDispatch,
    navigateStatusPageTarget,
    navigateErrorPageTarget,
  ]);

  return (
    <>
      <ForegroundLoadingSpinner />
    </>
  );
}
